import React, { useEffect, useRef, useState } from 'react';

import useRouter from 'app-migration/hooks/useRouter';
import clsx from 'clsx';
import { createUseStyles } from 'react-jss';

import Box, { BoxProps } from 'components/DesignSystem/Box/Box';
import { useStores } from 'hooks/use-stores';

import IconButton from '../Button/IconButton';
import Text from '../Text';
import Border from '../Theme/Border';
import Breakpoint from '../Theme/Breakpoint';
import Padding from '../Theme/Padding';
import Palette from '../Theme/Palette';
import Spacing from '../Theme/Spacing';

const useStyles = createUseStyles({
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        boxSizing: 'border-box',
        transition: 'opacity .2s ease .1s',
    },
    compactHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: Spacing(2),
        width: '100%',
        boxSizing: 'border-box',
    },
    centeredHeader: {
        textAlign: 'center',
    },
    hidden: { opacity: 0 },
});

interface BaseHeaderProps extends BoxProps {
    title?: string;
    subtitle?: string | JSX.Element | null;
    stickyHeaderContent?: JSX.Element | null;
    headerContent?: JSX.Element;
    eyebrowComponent?: JSX.Element;
    eyebrow?: string;
    compact?: boolean;
    compactTrailingAccessory?: boolean;
    iconSameLine?: boolean;
    center?: boolean;
    titleColor?: string;
    hideBottomBorder?: boolean;
    tabs?: boolean;
}

interface HeaderWithLeadingClose extends BaseHeaderProps {
    onClose?: () => void;
    showBackButton?: never;
    onBack?: never;
}

interface HeaderWithLeadingBack extends BaseHeaderProps {
    onClose?: never;
    showBackButton?: boolean;
    onBack?: () => void;
}

interface HeaderWithOneOrZeroAccessories extends BaseHeaderProps {
    trailingAccessory?: JSX.Element;
    secondTrailingAccessory?: never;
    thirdTrailingAccessory?: JSX.Element;
}

interface HeaderWithTwoAccessories extends BaseHeaderProps {
    trailingAccessory: JSX.Element;
    secondTrailingAccessory?: JSX.Element;
    thirdTrailingAccessory?: JSX.Element;
}

interface HeaderWithThreeAccessories extends BaseHeaderProps {
    trailingAccessory: JSX.Element;
    secondTrailingAccessory: JSX.Element;
    thirdTrailingAccessory?: JSX.Element;
}

export type HeaderProps = (HeaderWithLeadingClose | HeaderWithLeadingBack) &
    (HeaderWithOneOrZeroAccessories | HeaderWithTwoAccessories | HeaderWithThreeAccessories);

const Header = ({
    title,
    subtitle,
    eyebrowComponent,
    eyebrow,
    center,
    showBackButton,
    onBack,
    onClose,
    trailingAccessory,
    compact = false,
    compactTrailingAccessory = false,
    iconSameLine = false,
    stickyHeaderContent = null,
    headerContent = undefined,
    secondTrailingAccessory,
    thirdTrailingAccessory,
    className,
    titleColor = Palette.Text.x10,
    hideBottomBorder = false,
    tabs = false,
    ...boxProps
}: HeaderProps): JSX.Element => {
    const router = useRouter();
    const { uiStore } = useStores();

    const headerRef = useRef<HTMLDivElement>(null);
    const [inScrolledMode, setInScrollMode] = useState(false);

    useEffect(() => {
        const onScroll = (): void => {
            if (headerRef.current) {
                const { top, height } = headerRef.current.getBoundingClientRect();
                const headerHeight = uiStore.headerHeight;
                setInScrollMode(top + height - headerHeight < 0);
            }
        };
        window.removeEventListener('scroll', onScroll);
        window.addEventListener('scroll', onScroll, { passive: false });
        return (): void => window.removeEventListener('scroll', onScroll);
    }, [uiStore.headerHeight]);

    const classes = useStyles();

    const functionButton = (!!onClose || !!onBack || showBackButton) && (
        <IconButton
            icon={showBackButton || !!onBack ? 'ArrowBack' : 'Close'}
            rank={'tertiary'}
            size={'small'}
            onClick={!!onBack ? onBack : showBackButton ? router.back : onClose}
            buttonProps={{
                style: {
                    marginLeft: Spacing(-1),
                    marginBottom: compact ? Spacing(-1) : 0,
                    marginTop: Spacing(-1),
                },
            }}
        />
    );
    const eyebrowComponentEffective =
        eyebrowComponent ?? (!!eyebrow && <Text.Eyebrow.x10 color={Palette.Text.x20}>{eyebrow}</Text.Eyebrow.x10>);
    const TitleTextComponent = compact ? Text.Heading.x10 : !!center ? Text.Heading.x20 : Text.Heading.x30;
    const titleComponent = iconSameLine ? (
        <Box display={'flex'} justifyContent={'space-between'}>
            <TitleTextComponent headingLevel={!!center ? 'h2' : 'h1'} color={titleColor} className={`whitespace-nowrap`}>
                {title}
            </TitleTextComponent>
            {trailingAccessory}
        </Box>
    ) : (
        <TitleTextComponent headingLevel={!!center ? 'h2' : 'h1'} color={titleColor} className={`whitespace-nowrap`}>
            {title}
        </TitleTextComponent>
    );
    const subtitleComponent =
        !!subtitle &&
        (!!center ? (
            <Text.Body.x30 color={titleColor}>{subtitle}</Text.Body.x30>
        ) : (
            <Text.Body.x40 color={titleColor}>{subtitle}</Text.Body.x40>
        ));
    const accessories = !!trailingAccessory ? (
        <Box
            display={'flex'}
            mr={-1}
            mt={-1}
            mb={compact || compactTrailingAccessory ? -1 : 0}
            style={{ flexGrow: 0, alignSelf: 'flex-start', gap: Spacing(0.5) }}
        >
            {trailingAccessory}
            {secondTrailingAccessory}
            {thirdTrailingAccessory}
        </Box>
    ) : compact && !!title ? (
        <div style={{ width: '40px' }} />
    ) : undefined;

    const isSM = Breakpoint.isWidthDown.sm();
    useEffect(
        () => {
            if (!isSM && !!stickyHeaderContent) {
                uiStore.setTopHeaderReplacementContent(inScrolledMode ? stickyHeaderContent : null);
            }

            return (): void => {
                if (!!stickyHeaderContent) {
                    uiStore.setTopHeaderReplacementContent(null);
                }
            };
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [inScrolledMode, stickyHeaderContent],
    );

    if (compact) {
        return !!functionButton || !!title || !!trailingAccessory || !!headerContent || !!accessories ? (
            <Box
                className={clsx(className, classes.compactHeader)}
                style={{
                    textAlign: center ? 'center' : undefined,
                    borderBottom: hideBottomBorder ? undefined : Border(),
                    paddingBottom: tabs ? 0 : undefined,
                    paddingTop: tabs ? 8 : undefined,
                }}
                {...boxProps}
            >
                {/* Left cluster */}
                <Box display={'flex'} mr={!!accessories ? 0.5 : 0} style={{ flex: 1, alignItems: 'center' }}>
                    {!!functionButton && (
                        <>
                            {functionButton}
                            <Padding spacing={0.5} />
                        </>
                    )}
                    {!center && titleComponent}
                </Box>
                {/* Center cluster: show title when present, otherwise headerContent */}
                {center && <Box style={{ flex: 0 }}>{!!title ? titleComponent : headerContent}</Box>}
                {/* Right cluster */}
                <Box display={'flex'} style={{ flex: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                    {/* Keep headerContent on the right only when a title exists (so we don't duplicate when centering headerContent) */}
                    {(!center || !!title) && headerContent}
                    {!iconSameLine && accessories}
                </Box>
            </Box>
        ) : (
            <></>
        );
    }

    return (
        <Box
            className={clsx(
                className,
                classes.header,
                inScrolledMode && !isSM && classes.hidden,
                !!center && classes.centeredHeader,
            )}
            ref={headerRef}
            {...boxProps}
        >
            <div style={{ width: '100%' }}>
                {
                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignContent: 'center',
                            placeContent: showBackButton ? 'center space-between' : 'end',
                        }}
                    >
                        {functionButton}
                        {!iconSameLine && accessories}
                    </div>
                }
                {!!functionButton && !center && <Padding spacing={1} />}
                {eyebrowComponentEffective}
                {!!eyebrowComponent && <Padding spacing={1} />}
                {titleComponent}
                {!!titleComponent && !!subtitleComponent && <Padding spacing={1} />}
                {subtitleComponent}
            </div>
        </Box>
    );
};

export default Header;
